import { callApi } from '../../util/apiWrapper';
import { logException } from '../../services/appInsights';

const isObject = (v) => v != null && typeof v === 'object';
const DEV = typeof process !== 'undefined' && process?.env?.NODE_ENV !== 'production';

export const deriveUserId = (s) => {
  if (!isObject(s)) return null;

  const candidate =
    s.userId ??
    s.userID ??
    s.user_id ??
    s.userid ??
    (isObject(s.user) ? s.user.id ?? s.user.userId ?? s.user.user_id : null) ??
    (isObject(s.user?.result) ? s.user.result.id ?? s.user.result.userId ?? s.user.result.user_id : null) ??
    null;

  const n = Number(candidate);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const normalizeSenior = (s) => {
  if (!isObject(s)) return s;
  const userId = deriveUserId(s);
  return userId && s.userId == null ? { ...s, userId } : s;
};

export async function fetchSeniors({ auth, store, roleId = 1 } = {}) {
  if (!store) return [];
  try {
    store.setLoading?.(true);
    store.setSearching?.(true);

    const seniorData = await callApi(auth, `student?roleId=${roleId}`, 'GET');

    if (DEV && !Array.isArray(seniorData?.result)) {
      console.warn('[fetchSeniors] Unexpected response shape:', {
        hasResult: Object.prototype.hasOwnProperty.call(seniorData ?? {}, 'result'),
        resultType: typeof seniorData?.result,
        keys: isObject(seniorData) ? Object.keys(seniorData) : null,
      });
    }

    let apiSeniorsRaw = Array.isArray(seniorData?.result) ? seniorData.result : [];

    // Filter by site in store.staffsites
    if (Array.isArray(store.staffsites) && store.staffsites.length > 0) {
      const normalizeSite = (v) => (v == null ? '' : String(v).trim().toLowerCase());
      const staffSiteSet = new Set(store.staffsites.map(normalizeSite).filter(Boolean));
      apiSeniorsRaw = apiSeniorsRaw.filter((s) => staffSiteSet.has(normalizeSite(s?.site)));
    }

    // Filter by int_id in store.staffstudents
    if (Array.isArray(store.staffstudents) && store.staffstudents.length > 0) {
      const normalizeId = (id) => (id == null ? '' : String(id).trim());
      const staffStudentIds = new Set(store.staffstudents.map(normalizeId).filter(Boolean));
      apiSeniorsRaw = apiSeniorsRaw.filter((s) => staffStudentIds.has(normalizeId(s?.int_id)));
    }

    const apiSeniors = apiSeniorsRaw.map(normalizeSenior).filter(isObject);

    if (DEV && apiSeniors.length) {
      const sample = apiSeniors[0];
      const missingUserIdCount = apiSeniors.slice(0, 25).filter((s) => !deriveUserId(s)).length;
      console.debug('[fetchSeniors] sample row keys:', Object.keys(sample || {}));
      console.debug('[fetchSeniors] user shape:', {
        userKeys: isObject(sample?.user) ? Object.keys(sample.user) : null,
        userResultKeys: isObject(sample?.user?.result) ? Object.keys(sample.user.result) : null,
        derivedUserId: deriveUserId(sample),
        missingUserIdCountFirst25: missingUserIdCount,
      });
    }

    store.setSenior?.(null);
    store.setSeniorData?.(apiSeniors);

    return apiSeniors;
  } catch (error) {
    logException(error);
    if (DEV) console.error('[fetchSeniors] failed:', error);
    return [];
  } finally {
    store.setSearching?.(false);
    store.setLoading?.(false);
  }
}

export function makeInnerSort({ key, order = 'asc', standarizeEmail, dateToYYYYMM, dateUtcToyyyyMMdd, getSpecialistInfo } = {}) {
  return function innerSort(a, b) {
    if (!key) return 0;

    const dir = order === 'desc' ? -1 : 1;
    const A = isObject(a) ? a : {};
    const B = isObject(b) ? b : {};

    if (key === 'user') {
      const aEmail = A?.user?.email ?? A?.user?.result?.email ?? A?.[key]?.email ?? '';
      const bEmail = B?.user?.email ?? B?.user?.result?.email ?? B?.[key]?.email ?? '';
      const aVal = String(standarizeEmail?.(aEmail) ?? aEmail ?? '');
      const bVal = String(standarizeEmail?.(bEmail) ?? bEmail ?? '');
      if (aVal > bVal) return 1 * dir;
      if (aVal < bVal) return -1 * dir;
      return 0;
    }

    if (key === 'aftercare_specialist') {
      const aVal = String(getSpecialistInfo?.(A?.[key]) ?? '');
      const bVal = String(getSpecialistInfo?.(B?.[key]) ?? '');
      if (aVal > bVal) return 1 * dir;
      if (aVal < bVal) return -1 * dir;
      return 0;
    }

    if (key === 'graduation_date') {
      const aVal = A?.[key] ? String(dateToYYYYMM?.(A[key]) ?? '000000') : '000000';
      const bVal = B?.[key] ? String(dateToYYYYMM?.(B[key]) ?? '000000') : '000000';
      if (aVal > bVal) return 1 * dir;
      if (aVal < bVal) return -1 * dir;
      return 0;
    }

    if (key === 'dob') {
      const aVal = A?.[key] ? String(dateUtcToyyyyMMdd?.(A[key]) ?? '00000000') : '00000000';
      const bVal = B?.[key] ? String(dateUtcToyyyyMMdd?.(B[key]) ?? '00000000') : '00000000';
      if (aVal > bVal) return 1 * dir;
      if (aVal < bVal) return -1 * dir;
      return 0;
    }

    const aVal = String(A?.[key] ?? '');
    const bVal = String(B?.[key] ?? '');
    if (aVal > bVal) return 1 * dir;
    if (aVal < bVal) return -1 * dir;
    return 0;
  };
}

const mergeSenior = (prev, next) => {
  const p = isObject(prev) ? prev : {};
  const n = isObject(next) ? next : {};
  const merged = { ...p, ...n };

  const prevUser = isObject(p.user) ? p.user : null;
  const nextUser = isObject(n.user) ? n.user : null;

  if (nextUser && (!prevUser || nextUser?.email || nextUser?.result?.email)) merged.user = nextUser;
  else if (prevUser) merged.user = prevUser;

  const userId = deriveUserId(merged);
  if (userId && merged.userId == null) merged.userId = userId;

  return merged;
};

const scoreSeniorForDedup = (s) => {
  if (!isObject(s)) return 0;
  const userId = deriveUserId(s);
  const hasUserObj = !!(s.user || s.user?.result);
  const email = s?.user?.email ?? s?.user?.result?.email ?? null;
  return (userId ? 10 : 0) + (hasUserObj ? 3 : 0) + (email ? 5 : 0);
};

export function createUniqueList(list, key = 'int_id') {
  const src = Array.isArray(list) ? list : [];
  const bestByKey = new Map();

  for (const itemRaw of src) {
    if (!isObject(itemRaw)) continue;
    const item = normalizeSenior(itemRaw);

    const raw = item?.[key] ?? item?.id;
    const k = raw == null ? '' : String(raw).trim();
    if (!k) continue;

    const prev = bestByKey.get(k);
    if (!prev) {
      bestByKey.set(k, item);
      continue;
    }

    const merged = mergeSenior(prev, item);
    bestByKey.set(k, scoreSeniorForDedup(merged) >= scoreSeniorForDedup(prev) ? merged : prev);
  }

  const out = Array.from(bestByKey.values()).map(normalizeSenior).filter(isObject);

  if (DEV && src.length && out.length === 0) {
    console.warn('[createUniqueList] produced 0 rows from non-empty input', {
      inputLen: src.length,
      key,
      sample: src[0],
    });
  }

  if (DEV && out.length && out.length < Math.min(src.length, 5)) {
    console.warn('[createUniqueList] large reduction; check key collisions', {
      inputLen: src.length,
      outputLen: out.length,
      key,
    });
  }

  return out;
}
